import { FC, Fragment } from 'react';
import { Control, useController, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { EditDocumentIcon } from '../../../../../../assets/icons/index';
import InfoCircle, { InfoCircleVariant } from '../../../../../../assets/icons/info-circle';
import { SendDocumentIcon } from '../../../../../../assets/icons/send-document-icon';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { Switch } from '../../../../../../components/switch/index';
import { Cursor, Tooltip } from '../../../../../../components/tooltip/index';
import { expirationPeriodOptions } from '../../../../templates/constants/add-template-form-options.constant';
import {
  SigningMode,
  signingModeDescriptionKey,
  signingModeLabelKey,
} from '../../../../templates/enums/template.const';
import { QuickSignFormSchema } from '../../../validation/quick-sign.validation';

const SIGNING_MODE_OPTIONS: {
  icon: FC<{ className?: string; iconColor?: string }>;
  value: SigningMode;
}[] = [
  { value: SigningMode.ALL_AT_ONCE, icon: SendDocumentIcon },
  { value: SigningMode.SET_ORDER, icon: EditDocumentIcon },
];

export type QuickSignStepTwoProps = {
  control: Control<QuickSignFormSchema>;
  setValue: UseFormSetValue<QuickSignFormSchema>;
};

export const QuickSignStepTwo: FC<QuickSignStepTwoProps> = ({ control, setValue }) => {
  const signingMode = useWatch({ control, name: 'stepTwo.signingMode' });
  const expireDocument = useWatch({ control, name: 'stepTwo.expireDocument' });
  const expirationPeriod = useWatch({ control, name: 'stepTwo.expirationPeriod' });

  const { field: signingModeField } = useController({ control, name: 'stepTwo.signingMode' });
  const { field: signingOrderMattersField } = useController({
    control,
    name: 'stepTwo.signingOrderMatters',
  });
  const { field: expireDocumentField } = useController({
    control,
    name: 'stepTwo.expireDocument',
  });
  const { field: expirationPeriodField, fieldState: expirationPeriodFieldState } = useController({
    control,
    name: 'stepTwo.expirationPeriod',
  });

  const handleSigningModeChange = (value: SigningMode) => {
    signingModeField.onChange(value);
    signingOrderMattersField.onChange(value === SigningMode.SET_ORDER);
  };

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-4 pt-6">
        <div className="flex flex-col gap-4">
          <span className="text-xs text-gray-500">
            <AppFormattedMessage id="SIGNING_MODE" />
          </span>
          <div className="flex gap-4">
            {SIGNING_MODE_OPTIONS.map(({ value, icon: Icon }) => {
              const isSelected = signingMode === value;
              const labelKey = signingModeLabelKey[value];
              const descKey = signingModeDescriptionKey[value];

              return (
                <button
                  className={twMerge(
                    'flex flex-1 flex-col gap-3 rounded-lg border-2 p-4 text-left transition-colors',
                    isSelected
                      ? 'border-brand-50 bg-brand-25 text-brand-700'
                      : 'border-gray-100 bg-white text-gray-700',
                  )}
                  key={value}
                  onClick={() => handleSigningModeChange(value)}
                  type="button"
                >
                  <Icon
                    className="h-6 w-6 shrink-0"
                    iconColor={isSelected ? '#2565C8' : '#344054'}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm">
                      <AppFormattedMessage id={labelKey} />
                    </span>
                    <span
                      className={twMerge(
                        'text-[10px] leading-4',
                        isSelected ? 'text-brand-600' : 'text-gray-400',
                      )}
                    >
                      <AppFormattedMessage id={descKey} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">
              <AppFormattedMessage id="EXPIRATION_OPTIONAL" />
            </span>
            <Tooltip
              className="w-[480px]"
              content={<AppFormattedMessage id="EXPIRATION_TOOLTIP" />}
              cursor={Cursor.POINTER}
              placement="top"
            >
              <button
                className="flex shrink-0 items-center justify-center text-gray-400 hover:text-gray-600"
                type="button"
              >
                <InfoCircle
                  className="size-4 text-gray-400 hover:text-gray-600"
                  iconColor="currentColor"
                  variant={InfoCircleVariant.EVENTS}
                />
              </button>
            </Tooltip>
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-3">
            <div className="flex items-center gap-3">
              <Switch
                checked={expireDocument}
                onChange={(value) => {
                  expireDocumentField.onChange(value);
                  if (!value) {
                    setValue('stepTwo.expirationPeriod', undefined);
                  }
                }}
              />
              <span className="text-sm text-gray-700">
                <AppFormattedMessage id="EXPIRE_DOCUMENT" />
              </span>
            </div>

            {expireDocument && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  {expirationPeriodOptions.map((option, index) => {
                    const isSelected = expirationPeriod === option.value;
                    const isLast = index === expirationPeriodOptions.length - 1;

                    return (
                      <Fragment key={option.value}>
                        <button
                          className={twMerge(
                            'h-10 flex-1 px-2 text-[12px] transition-colors',
                            isSelected
                              ? 'bg-brand-700 text-[14px] text-white'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-50',
                          )}
                          onClick={() => expirationPeriodField.onChange(option.value)}
                          type="button"
                        >
                          <AppFormattedMessage id={option.labelKey} />
                        </button>
                        {!isLast && <div aria-hidden className="h-10 w-px shrink-0 bg-gray-200" />}
                      </Fragment>
                    );
                  })}
                </div>
                {expirationPeriodFieldState.error?.message && (
                  <span className="text-xs text-fireside-500">
                    {expirationPeriodFieldState.error.message}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
